import { useCallback, useEffect, useRef, useState } from "react";
import { log } from "@connect/observability";
import { createAuthenticatedPublicClient } from "@connect/data/public/client";
import type { components } from "@connect/data/public/schema";
import { customerApiHeaders } from "./customer-api-headers";

type IdentityFileType = components["schemas"]["IdentityFileType"];
type IdentityFileSide = components["schemas"]["IdentityFileSide"];
type IdentityFileMimeType = components["schemas"]["IdentityFileMimeType"];
type IdentityFileSubmission = components["schemas"]["IdentityFileSubmission"];
type IdentityFileUploadUrlHeaders = components["schemas"]["IdentityFileUploadUrlHeaders"];

export const TWO_SIDED_FILE_TYPES = new Set<IdentityFileType>([
  "drivingLicence",
  "nationalIdentityCard",
  "residencePermit",
]);

export interface FileUploadPart {
  file: File;
  documentType: IdentityFileType;
  side?: IdentityFileSide;
}

export interface UseFileUploadParams {
  customerId: string | null | undefined;
  apiBaseUrl: string;
  clientHeaders: Record<string, string>;
  /** Called after files are registered so the app can refresh KYC requirements. */
  onSuccess?: () => void | Promise<void>;
}

export interface UseFileUploadResult {
  uploading: boolean;
  progress: number;
  error: Error | null;
  upload: (file: File, documentType: string, side?: IdentityFileSide) => Promise<boolean>;
  uploadAll: (parts: FileUploadPart[]) => Promise<boolean>;
  retry: () => Promise<boolean>;
}

export function mimeTypeFromFile(file: File): IdentityFileMimeType {
  if (file.type === "image/png") {
    return "image/png";
  }
  if (file.type === "application/pdf") {
    return "application/pdf";
  }
  return "image/jpeg";
}

export function putFileToPresignedUrl(
  url: string,
  file: File,
  headers: IdentityFileUploadUrlHeaders,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      reject(new Error("upload_failed"));
    };
    xhr.onerror = () => reject(new Error("upload_failed"));
    xhr.send(file);
  });
}

async function requestUploadUrl(
  customerId: string,
  apiBaseUrl: string,
  clientHeaders: Record<string, string>,
  part: FileUploadPart,
) {
  const client = createAuthenticatedPublicClient({
    baseUrl: apiBaseUrl,
    headers: await customerApiHeaders(clientHeaders),
  });
  const mimeType = mimeTypeFromFile(part.file);
  return client.POST("/platform/v1/customers/{id}/files/upload-url", {
    params: { path: { id: customerId } },
    body: {
      fileType: part.documentType,
      mimeType,
      ...(part.side ? { side: part.side } : {}),
    },
  });
}

async function registerFiles(
  customerId: string,
  apiBaseUrl: string,
  clientHeaders: Record<string, string>,
  files: IdentityFileSubmission[],
) {
  const client = createAuthenticatedPublicClient({
    baseUrl: apiBaseUrl,
    headers: await customerApiHeaders(clientHeaders),
  });
  return client.POST("/platform/v1/customers/{id}/files", {
    params: { path: { id: customerId } },
    body: { files },
  });
}

export function useFileUpload({
  customerId,
  apiBaseUrl,
  clientHeaders,
  onSuccess,
}: UseFileUploadParams): UseFileUploadResult {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const lastPartsRef = useRef<FileUploadPart[] | null>(null);
  const requestRef = useRef({ apiBaseUrl, clientHeaders, customerId });
  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    requestRef.current = { apiBaseUrl, clientHeaders, customerId };
    onSuccessRef.current = onSuccess;
  });

  const uploadAll = useCallback(async (parts: FileUploadPart[]): Promise<boolean> => {
    lastPartsRef.current = parts;
    const { customerId: id, apiBaseUrl: baseUrl, clientHeaders: headers } = requestRef.current;
    if (!id) {
      setError(new Error("kyc_upload_failed"));
      return false;
    }
    if (parts.length === 0) {
      setError(new Error("kyc_upload_failed"));
      return false;
    }

    setUploading(true);
    setError(null);
    setProgress(0);

    try {
      const submissions: IdentityFileSubmission[] = [];
      for (let index = 0; index < parts.length; index += 1) {
        const part = parts[index];
        if (!part) {
          continue;
        }
        const { data, error: urlError } = await requestUploadUrl(id, baseUrl, headers, part);
        if (urlError || !data?.data) {
          log.warn("connect.onboarding.file_upload_failed", { reason: "upload_url" });
          setError(new Error("kyc_upload_failed"));
          return false;
        }

        const rangeStart = (index / parts.length) * 100;
        const rangeSize = 100 / parts.length;
        await putFileToPresignedUrl(data.data.url, part.file, data.data.headers, (percent) => {
          setProgress(Math.round(rangeStart + (percent / 100) * rangeSize));
        });
        submissions.push({
          uploadId: data.data.uploadId,
          fileType: part.documentType,
          ...(part.side ? { side: part.side } : {}),
        });
      }

      const { data: registered, error: registerError } = await registerFiles(
        id,
        baseUrl,
        headers,
        submissions,
      );
      if (registerError || !registered?.data) {
        log.warn("connect.onboarding.file_upload_failed", { reason: "register" });
        setError(new Error("kyc_upload_failed"));
        return false;
      }

      setProgress(100);
      try {
        await onSuccessRef.current?.();
      } catch (caught) {
        log.warn("connect.onboarding.kyc_refetch_failed", {
          reason: caught instanceof Error ? caught.message : "network_error",
        });
      }
      return true;
    } catch (caught) {
      log.warn("connect.onboarding.file_upload_failed", {
        reason: caught instanceof Error ? caught.message : "network_error",
      });
      setError(caught instanceof Error ? caught : new Error(String(caught)));
      return false;
    } finally {
      setUploading(false);
    }
  }, []);

  const upload = useCallback(
    (file: File, documentType: string, side?: IdentityFileSide) => {
      return uploadAll([
        {
          file,
          documentType: documentType as IdentityFileType,
          side,
        },
      ]);
    },
    [uploadAll],
  );

  const retry = useCallback(async () => {
    const parts = lastPartsRef.current;
    if (!parts) {
      return false;
    }
    return uploadAll(parts);
  }, [uploadAll]);

  return { uploading, progress, error, upload, uploadAll, retry };
}
